// Per-browser session_id used for single-session enforcement.
//
// The server keeps one active session_id per user (see
// `tres-api/src/session_registry.rs`). The SPA generates a fresh UUID
// at OIDC sign-in callback and ships it on every authenticated
// request via the X-Session-Id header. Same-browser tabs share the
// value through localStorage; a sign-in from a different browser /
// device generates a different UUID and takes over server-side, at
// which point the previously-active session's next request returns
// 401 `session_superseded` and the SPA signs that browser out.

const STORAGE_KEY = 'tres.session_id';

/**
 * Generate a new session_id and store it. Called from the OIDC
 * sign-in callback so every fresh sign-in (even within the same
 * browser) produces a clean handle.
 */
export function generateAndStoreSessionId(): string {
  // crypto.randomUUID is available on all modern browsers TReS
  // supports (Chrome 92+, Safari 15.4+, Firefox 95+). Cognito Hosted
  // UI requires similarly-modern browsers, so no polyfill needed.
  const id = crypto.randomUUID();
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // localStorage may be unavailable in incognito with restrictive
    // settings. The request will still send the header for the
    // current tab; cross-tab coordination just won't work. Logging
    // here would be noisy on every reload; rely on the SPA's later
    // 401 handling instead.
  }
  return id;
}

/**
 * Read the current session_id, returning null if none is stored.
 * The SPA must NEVER auto-generate a session_id on read — that would
 * defeat the "every sign-in gets a fresh handle" guarantee. Read
 * returns null → caller should redirect to sign-in.
 */
export function getSessionId(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Clear the stored session_id. Called on sign-out (manual or forced
 * by idle timeout / superseded).
 */
export function clearSessionId(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
